// After the event (kitchen flow, Stage 2D): for each item on the plan, how
// much the dishes used and what happens to what is left — keep, send back to
// the store, or throw away (manager PIN). One wrap-up per plan.
// Strings: lib/inventoryFlow/wrapUpStrings.ts. Rules: lib/inventoryFlow/wrapUpLogic.ts.
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { friendlyError } from '@/lib/inventoryFlow/friendlyError'
import { T } from '@/lib/inventoryFlow/strings'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { formatMoney } from '@/lib/currency'
import { cn, formatDateTimeHuman } from '@/lib/utils'
import { newIdempotencyKey } from '@/lib/inventoryFlow/offlineQueue'
import { fmtAmount } from '@/lib/inventoryFlow/planLogic'
import { WU } from '@/lib/inventoryFlow/wrapUpStrings'
import type { PlanWrapUp, WrapUpReason } from '@/lib/inventoryFlow/wrapUpTypes'
import { canSubmitWrapUp, initialWrapUp, leftAfter, needsPin, wrapUpBody, type WrapUpDraft } from '@/lib/inventoryFlow/wrapUpLogic'
import { QtyStepper } from './QtyStepper'
import { LoadError } from './LoadError'

type Notify = (kind: 'success' | 'error', message: string) => void

function errMessage(e: unknown): string {
  return friendlyError(e, { brief: true })
}

export function WrapUpSummary({ w }: { w: PlanWrapUp }) {
  return (
    <section className="space-y-2 rounded-lg border-2 border-emerald-300 p-4 dark:border-emerald-800">
      <h3 className="text-base font-semibold">{WU.summaryTitle}</h3>
      <p className="text-sm text-muted-foreground">{WU.summaryBy(w.wrapped_up_by ?? '', formatDateTimeHuman(w.wrapped_up_at))}{w.note ? ` · ${w.note}` : ''}</p>
      <div className="flex flex-wrap gap-4 text-sm">
        <span>{WU.usedValue}: <strong className="tabular-nums">{formatMoney(w.used_value)}</strong></span>
        <span>{WU.wasteValue}: <strong className="tabular-nums">{formatMoney(w.waste_value)}</strong></span>
        {w.return_requisition_no && <span>{WU.returnReq(w.return_requisition_no)}</span>}
        {w.equipment_out > 0 && <span className="font-semibold text-destructive">{WU.equipmentStillOut(w.equipment_out)}</span>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-muted-foreground">
            <tr><th className="p-1.5">{WU.colItem}</th><th className="p-1.5 text-right">{WU.colUsed}</th><th className="p-1.5 text-right">{WU.colKeep}</th><th className="p-1.5 text-right">{WU.colBack}</th><th className="p-1.5 text-right">{WU.colThrow}</th></tr>
          </thead>
          <tbody className="divide-y">
            {w.lines.map((l) => (
              <tr key={l.stock_item_id}>
                <td className="p-1.5 font-medium">{l.name}</td>
                {[l.used, l.kept, l.returned, l.wasted].map((q, i) => <td key={i} className="p-1.5 text-right tabular-nums">{q > 0 ? `${fmtAmount(q)} ${l.unit}` : '—'}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

export function WrapUpPanel({ planId, notify, onClose }: { planId: string; notify: Notify; onClose: () => void }) {
  const qc = useQueryClient()
  const { data, isLoading, isError, refetch, isFetching } = useQuery({ queryKey: ['inventory-flow', 'plans', planId, 'wrap-up'], queryFn: () => apiClient.getPlanWrapUp(planId) })
  const sheet = data?.data
  const [drafts, setDrafts] = useState<Record<string, WrapUpDraft>>({})
  const [pin, setPin] = useState('')
  const [note, setNote] = useState('')
  const [ackOpen, setAckOpen] = useState(false)
  const [key] = useState(newIdempotencyKey)
  useEffect(() => {
    if (sheet) setDrafts(initialWrapUp(sheet.lines))
  }, [sheet?.plan_id]) // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: () => apiClient.wrapUpPlan(planId, wrapUpBody(drafts, pin, note, ackOpen), key),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory-flow'] })
      notify('success', WU.done)
      onClose()
    },
    onError: (e) => notify('error', errMessage(e)),
  })

  if (isLoading) return <p className="text-muted-foreground">{T.loading}</p>
  if (isError || !sheet) return <LoadError busy={isFetching} onRetry={() => void refetch()} />
  const set = (id: string, patch: Partial<WrapUpDraft>) => setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }))
  const out = sheet.returnables.filter((r) => r.outstanding > 1e-6)
  const openBatches = sheet.open_batches ?? []

  return (
    <section className="space-y-4 rounded-lg border-2 border-blue-300 p-4 dark:border-blue-800">
      <div>
        <h3 className="text-lg font-bold">{WU.title}</h3>
        <p className="text-muted-foreground">{WU.help}</p>
      </div>
      {openBatches.length > 0 && (
        <div className="space-y-2 rounded-lg border border-amber-400 bg-amber-50 p-3 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          <ul className="space-y-1 text-sm font-semibold">
            {openBatches.map((b) => <li key={b.id}>{WU.openBatch(b.prep_name)} <span className="font-normal">{b.batch_no} · {fmtAmount(b.planned_qty)} {b.unit}</span></li>)}
          </ul>
          <p className="text-xs">{WU.openBatchHint}</p>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="h-4 w-4" checked={ackOpen} onChange={(e) => setAckOpen(e.target.checked)} />
            {WU.acknowledgeOpen}
          </label>
        </div>
      )}
      {sheet.lines.length === 0 && <p className="text-muted-foreground">{WU.nothing}</p>}
      <ul className="space-y-3">
        {sheet.lines.map((l) => {
          const d = drafts[l.stock_item_id]
          if (!d) return null
          const left = leftAfter(l.kitchen_on_hand, d)
          return (
            <li key={l.stock_item_id} className="space-y-2 rounded-lg border p-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-base font-semibold">{l.name}</span>
                <span className="text-sm text-muted-foreground">{WU.colPlanned} {fmtAmount(l.planned_use)} {l.unit} · {WU.colKitchen} {fmtAmount(l.kitchen_on_hand)} {l.unit}</span>
              </div>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {([['used', WU.colUsed], ['keep', WU.colKeep], ['send_back', WU.colBack], ['throw_away', WU.colThrow]] as const).map(([k, label]) => (
                  <label key={k} className="space-y-1">
                    <span className="text-sm font-medium">{label}</span>
                    <QtyStepper value={d[k]} unit={l.unit} label={`${label} ${l.name}`} min={0} onChange={(v) => set(l.stock_item_id, { [k]: v })} />
                  </label>
                ))}
              </div>
              {d.throw_away > 1e-6 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">{WU.reason}</span>
                  {(Object.keys(WU.reasons) as WrapUpReason[]).map((r) => (
                    <button key={r} type="button" aria-pressed={d.reason === r} onClick={() => set(l.stock_item_id, { reason: r })}
                      className={cn('h-10 rounded-lg border px-3 text-sm font-semibold touch-manipulation', d.reason === r ? 'border-primary bg-primary text-primary-foreground' : 'bg-card')}>
                      {WU.reasons[r]}
                    </button>
                  ))}
                </div>
              )}
              <p className={cn('text-sm', left < -1e-6 ? 'font-semibold text-destructive' : 'text-muted-foreground')}>{WU.leftAfter(fmtAmount(left), l.unit)}</p>
            </li>
          )
        })}
      </ul>
      {out.length > 0 && (
        <div className="rounded-lg border border-amber-400 bg-amber-50 p-3 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          <p className="font-semibold">{WU.equipmentOut}</p>
          <p className="text-sm">{out.map((r) => `${r.name} × ${fmtAmount(r.outstanding)}`).join(', ')}</p>
          <p className="text-xs">{WU.equipmentHint}</p>
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        {needsPin(drafts) && (
          <label className="space-y-1">
            <span className="text-sm font-medium">{WU.pin}</span>
            <Input type="password" inputMode="numeric" autoComplete="off" value={pin} onChange={(e) => setPin(e.target.value)} />
          </label>
        )}
        <label className="space-y-1">
          <span className="text-sm font-medium">{WU.note}</span>
          <Input placeholder={WU.notePlaceholder} value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
      </div>
      <div className="flex gap-2">
        <Button disabled={!canSubmitWrapUp(drafts, pin, { sheetEmpty: sheet.lines.length === 0, openBatches: openBatches.length, acknowledged: ackOpen }) || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? WU.saving : WU.submit}
        </Button>
        <Button variant="ghost" onClick={onClose}>{WU.cancel}</Button>
      </div>
    </section>
  )
}
